import React, { useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  Mail,
  History,
  MessageSquare,
  ChevronRight,
  LogOut,
  LogIn,
  Loader2,
  Trash2,
  X,
  Pencil,
  Bell,
  Image as ImageIcon,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUserStore, selectIsSessionValid } from '../store/userStore';
import { useUiPreferencesStore } from '../store/uiPreferencesStore';
import { useProfile, userProfileQueryKey } from '../hooks/useProfile';
import { userProfileApi } from '../services/userProfileApi';
import {
  isWebPushAvailable,
  getPushSubscriptionActive,
  enableWebPush,
  disableWebPush,
} from '../services/pushNotifications';
import { appPath, appLoginPath } from '../routes/appPaths';
import { LoyaltyCard } from '../components/LoyaltyCard';

function formatBirthday(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function SettingsScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useUserStore((state) => state.token);
  const logout = useUserStore((state) => state.logout);
  const setName = useUserStore((state) => state.setName);
  const menuShowImages = useUiPreferencesStore((s) => s.menuShowImages);
  const setMenuShowImages = useUiPreferencesStore((s) => s.setMenuShowImages);
  const isLoggedIn = useUserStore(selectIsSessionValid);

  const { data: user, isLoading, isError } = useProfile();

  const [profileModal, setProfileModal] = useState<'email' | 'birthday' | null>(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [birthdayDraft, setBirthdayDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const webPushAvailable = useMemo(() => isWebPushAvailable(), []);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');

  useEffect(() => {
    if (user?.firstName) setName(user.firstName);
  }, [user?.firstName, setName]);

  useEffect(() => {
    if (!token || !webPushAvailable) return;
    let cancelled = false;
    async function refreshPush() {
      const v = await getPushSubscriptionActive();
      if (!cancelled) setPushEnabled(v);
    }
    refreshPush();
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshPush();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [token, webPushAvailable]);

  async function handlePushToggle() {
    if (!token || pushBusy || !webPushAvailable) return;
    setPushBusy(true);
    setPushError('');
    try {
      if (pushEnabled) {
        await disableWebPush(token);
        setPushEnabled(false);
      } else {
        const ok = await enableWebPush(token);
        if (!ok) {
          setPushError('Не удалось включить. Разрешите уведомления в браузере.');
        }
        setPushEnabled(await getPushSubscriptionActive());
      }
    } finally {
      setPushBusy(false);
    }
  }

  function openEmailModal() {
    if (!user) return;
    setEmailDraft(user.email ?? '');
    setSaveError('');
    setProfileModal('email');
  }

  function openBirthdayModal() {
    if (!user) return;
    setBirthdayDraft(user.birthday ?? '');
    setSaveError('');
    setProfileModal('birthday');
  }

  async function handleSaveProfile() {
    if (!token || !profileModal) return;
    setSaving(true);
    setSaveError('');
    try {
      const body: { email?: string; birthday?: string } = {};
      if (profileModal === 'email') {
        const em = emailDraft.trim();
        if (!em) {
          setSaveError('Введите email');
          setSaving(false);
          return;
        }
        body.email = em;
      } else {
        if (!birthdayDraft) {
          setSaveError('Выберите дату');
          setSaving(false);
          return;
        }
        body.birthday = birthdayDraft;
      }
      const updated = await userProfileApi.patchMe(token, body);
      await queryClient.invalidateQueries({ queryKey: userProfileQueryKey(token) });
      if (updated.firstName) setName(updated.firstName);
      setProfileModal(null);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteAccount() {
    if (!token) return;
    if (!window.confirm('Удалить аккаунт безвозвратно? Все данные профиля будут удалены.')) return;
    setDeleting(true);
    try {
      await userProfileApi.deleteMe(token);
      logout();
      navigate('/', { replace: true });
    } catch {
      setDeleting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col pb-24" style={{ background: '#0a0a0a', color: '#fff' }}>
      <div className="max-w-md mx-auto w-full px-4 pt-6">
        <div className="flex items-center justify-between mb-8">
          <h1
            className="text-2xl font-bold uppercase"
            style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.03em' }}
          >
            Профиль
          </h1>
          <Link
            to="/"
            className="text-[11px] tracking-[0.16em] uppercase"
            style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
          >
            На сайт
          </Link>
        </div>

        {!isLoggedIn ? (
          <section
            className="rounded-3xl p-5 mb-6"
            style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }}
          >
            <p
              className="text-sm mb-4 leading-relaxed"
              style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Manrope, sans-serif' }}
            >
              Войдите, чтобы оформлять заказы, смотреть историю и получать уведомления.
            </p>
            <button
              type="button"
              onClick={() => navigate(appLoginPath(appPath('/settings')))}
              className="w-full py-3.5 rounded-2xl font-semibold text-sm tracking-[0.12em] uppercase active:scale-[0.99]"
              style={{ background: '#fff', color: '#0a0a0a', fontFamily: 'Manrope, sans-serif' }}
            >
              <span className="inline-flex items-center justify-center gap-2">
                <LogIn size={16} /> Войти в аккаунт
              </span>
            </button>
          </section>
        ) : (
          <>
            {!isLoading && user && token && (
              <LoyaltyCard
                token={token}
                firstName={user.firstName}
                lastName={user.lastName ?? null}
                phone={user.phone}
                bonuses={user.bonuses}
              />
            )}
            <section
              className="rounded-3xl p-5 mb-6"
              style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }}
            >
              {isLoading && (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="animate-spin" size={28} style={{ color: 'rgba(255,255,255,0.3)' }} />
                </div>
              )}
              {!isLoading && isError && (
                <div className="text-center py-8 px-2">
                  <p
                    className="text-sm mb-4 leading-relaxed"
                    style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
                  >
                    Сессия истекла или не удалось загрузить профиль. Войдите снова.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      logout();
                      queryClient.clear();
                      navigate(appLoginPath(appPath('/settings')));
                    }}
                    className="w-full py-3.5 rounded-2xl font-semibold text-sm tracking-[0.12em] uppercase active:scale-[0.99]"
                    style={{ background: '#fff', color: '#0a0a0a', fontFamily: 'Manrope, sans-serif' }}
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      <LogIn size={16} /> Войти снова
                    </span>
                  </button>
                </div>
              )}
              {!isLoading && user && (
                <>
                  <div className="flex items-center gap-4 mb-6">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-bold"
                      style={{ background: 'rgba(255,255,255,0.08)', fontFamily: 'Unbounded, sans-serif' }}
                    >
                      {(user.firstName || 'Г')[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-xl font-bold truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.firstName}
                      </h2>
                      <p className="text-sm truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>
                        {user.phone}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <ProfileFieldRow
                      icon={<Calendar size={18} />}
                      label="День рождения"
                      value={user.birthday ? formatBirthday(user.birthday) : 'Не указано'}
                      empty={!user.birthday}
                      onFill={openBirthdayModal}
                      onEdit={openBirthdayModal}
                    />
                    <div className="h-px mx-2" style={{ background: 'rgba(255,255,255,0.08)' }} />
                    <ProfileFieldRow
                      icon={<Mail size={18} />}
                      label="Почта"
                      value={user.email ?? 'Не указано'}
                      empty={!user.email}
                      onFill={openEmailModal}
                      onEdit={openEmailModal}
                    />
                  </div>
                </>
              )}
            </section>

            {webPushAvailable && (
              <section
                className="mb-6 rounded-3xl overflow-hidden"
                style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }}
              >
                <div className="flex items-center gap-3 p-4">
                  <div className="shrink-0" style={{ color: 'rgba(255,255,255,0.35)' }}>
                    <Bell size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">Push-уведомления</p>
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      Статусы заказа и напоминания
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={pushEnabled}
                    disabled={pushBusy}
                    onClick={handlePushToggle}
                    className="relative w-11 h-6 rounded-full transition-colors shrink-0 disabled:opacity-50"
                    style={{ background: pushEnabled ? '#fff' : 'rgba(255,255,255,0.2)' }}
                  >
                    <span
                      className={`absolute top-1 left-1 w-4 h-4 rounded-full shadow transition-transform ${
                        pushEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                      style={{ background: pushEnabled ? '#0a0a0a' : '#fff' }}
                    />
                  </button>
                </div>
                {pushBusy && (
                  <div className="flex justify-center pb-3">
                    <Loader2 className="animate-spin" size={18} style={{ color: 'rgba(255,255,255,0.3)' }} />
                  </div>
                )}
                {pushError && !pushBusy && (
                  <p className="text-xs text-red-400 px-4 pb-3">{pushError}</p>
                )}
              </section>
            )}
          </>
        )}

        <section
          className="mb-6 rounded-3xl overflow-hidden"
          style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }}
        >
          <div className="flex items-center gap-3 p-4">
            <div className="shrink-0" style={{ color: 'rgba(255,255,255,0.35)' }}>
              <ImageIcon size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Фото в меню</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {menuShowImages ? 'Сетка с картинками' : 'Компактный список без фото'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={menuShowImages}
              onClick={() => setMenuShowImages(!menuShowImages)}
              className="relative w-11 h-6 rounded-full transition-colors shrink-0"
              style={{ background: menuShowImages ? '#fff' : 'rgba(255,255,255,0.2)' }}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 rounded-full shadow transition-transform ${
                  menuShowImages ? 'translate-x-5' : 'translate-x-0'
                }`}
                style={{ background: menuShowImages ? '#0a0a0a' : '#fff' }}
              />
            </button>
          </div>
        </section>

        <div className="space-y-2">
          {isLoggedIn && (
            <MenuButton
              icon={<History size={20} />}
              label="История заказов"
              onClick={() => navigate(appPath('/orders'))}
            />
          )}
          <MenuButton
            icon={<MessageSquare size={20} />}
            label="Контакт поддержки"
            onClick={() => window.open('https://t.me/dmitrijvovchinskij', '_blank')}
          />
          {isLoggedIn && (
            <>
              <MenuButton
                icon={<Trash2 size={20} />}
                label="Удалить аккаунт"
                onClick={handleDeleteAccount}
                danger
                disabled={deleting}
              />
              <MenuButton
                icon={<LogOut size={20} />}
                label="Выйти"
                onClick={() => {
                  logout();
                  navigate(appPath('/home'));
                }}
                danger
              />
            </>
          )}
        </div>
      </div>

      {profileModal && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.45)' }}
          onClick={() => !saving && setProfileModal(null)}
        >
          <div
            className="w-full max-w-sm rounded-3xl p-5"
            style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.12)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">
                {profileModal === 'email' ? 'Почта' : 'День рождения'}
              </h3>
              <button
                type="button"
                disabled={saving}
                onClick={() => setProfileModal(null)}
                className="p-2"
                style={{ color: 'rgba(255,255,255,0.4)' }}
              >
                <X size={18} />
              </button>
            </div>
            {profileModal === 'email' ? (
              <input
                type="email"
                placeholder="Email"
                value={emailDraft}
                onChange={(e) => {
                  setEmailDraft(e.target.value);
                  setSaveError('');
                }}
                disabled={saving}
                className="w-full rounded-2xl px-4 py-3 mb-2 text-sm font-medium outline-none text-white"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
              />
            ) : (
              <input
                type="date"
                value={birthdayDraft}
                onChange={(e) => {
                  setBirthdayDraft(e.target.value);
                  setSaveError('');
                }}
                disabled={saving}
                className="w-full rounded-2xl px-4 py-3 mb-2 text-sm font-medium outline-none text-white"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', colorScheme: 'dark' }}
              />
            )}
            {saveError && <p className="text-xs text-red-400 mb-2">{saveError}</p>}
            <button
              type="button"
              disabled={saving}
              onClick={handleSaveProfile}
              className="w-full py-3.5 rounded-2xl font-semibold text-sm mt-2 active:scale-[0.99] disabled:opacity-50 tracking-[0.1em] uppercase"
              style={{ background: '#fff', color: '#0a0a0a' }}
            >
              {saving ? <Loader2 className="animate-spin mx-auto" size={20} /> : 'Сохранить'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileFieldRow({
  icon,
  label,
  value,
  empty,
  onFill,
  onEdit,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  empty: boolean;
  onFill: () => void;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-center gap-3 px-1">
      <div className="shrink-0" style={{ color: 'rgba(255,255,255,0.35)' }}>{icon}</div>
      <div className="flex-1 min-w-0">
        <p
          className="text-[10px] uppercase tracking-wider font-bold"
          style={{ color: 'rgba(255,255,255,0.3)' }}
        >
          {label}
        </p>
        <p
          className="text-sm font-semibold truncate"
          style={{ color: empty ? 'rgba(255,255,255,0.35)' : '#fff' }}
        >
          {value}
        </p>
      </div>
      <div className="shrink-0">
        {empty ? (
          <button
            type="button"
            onClick={onFill}
            className="text-xs font-semibold py-2 px-3 rounded-xl transition-all active:scale-95 shrink-0"
            style={{ background: '#fff', color: '#0a0a0a' }}
          >
            Заполнить
          </button>
        ) : (
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 transition-colors active:scale-95"
            style={{ color: 'rgba(255,255,255,0.4)' }}
            aria-label="Изменить"
          >
            <Pencil size={18} strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  );
}

function MenuButton({
  icon,
  label,
  onClick,
  danger,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-4 p-4 rounded-2xl active:scale-[0.98] transition-all disabled:opacity-50"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <div style={{ color: danger ? '#f87171' : '#fff' }}>{icon}</div>
      <span
        className="flex-1 text-left font-semibold text-sm"
        style={{ color: danger ? '#f87171' : '#fff', fontFamily: 'Manrope, sans-serif' }}
      >
        {label}
      </span>
      <ChevronRight size={18} style={{ color: 'rgba(255,255,255,0.2)' }} />
    </button>
  );
}
